import React, { useState, useEffect } from 'react';
import ChatEmbedWidget from './ChatEmbedWidget';
import ChatEmbedButton from './ChatEmbedButton';
import { Loader2 } from 'lucide-react';

const ChatEmbedPageContent = ({ agentId }) => {
    const [embedToken, setEmbedToken] = useState(null);
    const [viewMode, setViewMode] = useState(() => {
        if (typeof window === 'undefined') return 'full';
        const params = new URLSearchParams(window.location.search);
        const mode = params.get('view');
        if (mode === 'button' || mode === 'widget') return 'button';
        if (mode === 'minimal') return 'minimal';
        return 'full';
    });

    // Detect view mode and token from query string
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const mode = params.get('view');
        if (mode === 'button' || mode === 'widget') {
            setViewMode('button');
        } else if (mode === 'minimal') {
            setViewMode('minimal');
        } else {
            setViewMode('full');
        }

        const tokenParam = params.get('token') || params.get('embed_token') || params.get('key');
        if (tokenParam) {
            setEmbedToken(tokenParam);
        }
    }, []);

    // Transparent background for embedded modes so client site content shows through
    const embeddedMode = viewMode === 'button' || viewMode === 'minimal';
    useEffect(() => {
        if (!embeddedMode) return;
        const html = document.documentElement;
        const body = document.body;
        const root = document.getElementById('root');
        const prevHtmlBg = html.style.background;
        const prevBodyBg = body.style.background;
        const prevRootBg = root?.style?.background;
        html.style.background = 'transparent';
        body.style.background = 'transparent';
        if (root) root.style.background = 'transparent';
        return () => {
            html.style.background = prevHtmlBg;
            body.style.background = prevBodyBg;
            if (root) root.style.background = prevRootBg || '';
        };
    }, [embeddedMode]);

    // Button mode - floating chat button
    if (viewMode === 'button') {
        return (
            <div className="min-h-screen w-full bg-transparent">
                <ChatEmbedButton agentId={agentId} />
            </div>
        );
    }

    // Minimal mode - embedded widget
    if (viewMode === 'minimal') {
        return (
            <div className="min-h-[720px] w-full flex items-center justify-center bg-transparent p-4">
                <div className="w-full max-w-md h-[720px] max-h-[calc(100vh-2rem)]">
                    <ChatEmbedWidget agentId={agentId} embedToken={embedToken} />
                </div>
            </div>
        );
    }

    // Full mode - full page
    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-950 via-gray-900 to-gray-900 flex items-center justify-center p-4">
            <div className="w-full max-w-2xl h-[700px]">
                <ChatEmbedWidget agentId={agentId} embedToken={embedToken} />
            </div>
        </div>
    );
};

const ChatEmbedPage = () => {
    // Extract agentId from URL path
    const pathMatch = window.location.pathname.match(/^\/embed\/chat\/([^/]+)/);
    const agentId = pathMatch ? pathMatch[1] : null;

    if (!agentId) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-gray-950 via-gray-900 to-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <p className="text-destructive text-lg mb-4">Invalid agent ID</p>
                    <p className="text-muted-foreground">Please check the embed URL.</p>
                </div>
            </div>
        );
    }

    return <ChatEmbedPageContent agentId={agentId} />;
};

export { ChatEmbedPageContent };
export default ChatEmbedPage;

